import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, FormControl, InputLabel, Select,
  MenuItem, IconButton, CircularProgress, makeStyles,
  Divider, LinearProgress
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import PublishIcon from '@material-ui/icons/Publish';
import GetAppIcon from '@material-ui/icons/GetApp';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import { useSelector } from 'react-redux';
import { STATUS_API } from 'src/app/constant/config';

const useStyles = makeStyles(() => ({
  dialogTitle: {
    background: 'linear-gradient(135deg, #43a047 0%, #1b5e20 100%)',
    color: '#fff',
    padding: '16px 24px'
  },
  titleText: { fontWeight: 700, fontSize: 17, color: '#fff' },
  closeBtn: { color: '#fff' },
  dropZone: {
    border: '2px dashed #81c784',
    borderRadius: 10,
    padding: '28px 16px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': { borderColor: '#43a047', backgroundColor: '#f1f8e9' }
  },
  dropZoneActive: {
    borderColor: '#43a047',
    backgroundColor: '#f1f8e9'
  },
  resultBox: {
    borderRadius: 10,
    backgroundColor: '#f1f8e9',
    border: '1px solid #a5d6a7',
    padding: '14px 18px',
    marginTop: 14
  },
  resultItem: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 4,
    fontSize: 13
  },
  saveBtn: {
    background: 'linear-gradient(135deg, #43a047 0%, #1b5e20 100%)',
    color: '#fff',
    fontWeight: 700,
    borderRadius: 8,
    textTransform: 'none',
    '&:hover': { background: 'linear-gradient(135deg, #388e3c 0%, #1a5216 100%)' }
  }
}));

const ImportExcelDialog = ({
  open,
  onClose,
  agencies = [],
  defaultAgencyId = '',
  onImport,
  onDownloadTemplate
}) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);
  const { statusImport, importResult } = useSelector(state => state.productSlice);

  const [selectedFile, setSelectedFile] = useState(null);
  const [agencyId, setAgencyId] = useState(defaultAgencyId || '');
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedFile(null);
      setAgencyId(defaultAgencyId || '');
    }
  }, [open, defaultAgencyId]);

  const handleFilePick = e => {
    const file = e.target.files?.[0];
    if (file) setSelectedFile(file);
    e.target.value = '';
  };

  const handleDrop = e => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.name.endsWith('.xlsx')) setSelectedFile(file);
  };

  const handleDragOver = e => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleSubmit = () => {
    if (!selectedFile) return;
    onImport(selectedFile, agencyId || undefined);
  };

  const isLoading = statusImport === STATUS_API.PENDING;
  const isDone = statusImport === STATUS_API.SUCCESS && importResult;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography className={classes.titleText}>Nhập kho từ Excel</Typography>
          <IconButton size="small" className={classes.closeBtn} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box pt={1} display="flex" flexDirection="column" style={{ gap: 16 }}>
          {/* Template download */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            style={{ backgroundColor: '#e8f5e9', borderRadius: 8, padding: '10px 14px' }}
          >
            <Typography style={{ fontSize: 13, color: '#2e7d32' }}>
              File mẫu: <strong>mau_nhap_san_pham.xlsx</strong>
            </Typography>
            <Button
              size="small"
              startIcon={<GetAppIcon />}
              onClick={onDownloadTemplate}
              style={{ color: '#2e7d32', fontWeight: 700, textTransform: 'none' }}
            >
              Tải mẫu
            </Button>
          </Box>

          <Typography variant="caption" color="textSecondary" style={{ marginTop: -8 }}>
            Cột bắt buộc: <code>ma_sp, ten_sp, don_vi, bao_gom, mo_ta, so_luong</code>
          </Typography>

          {/* Agency selector */}
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Kho nhập (đại lý)</InputLabel>
            <Select
              value={agencyId}
              onChange={e => setAgencyId(e.target.value)}
              label="Kho nhập (đại lý)"
            >
              <MenuItem value="">Lấy từ tài khoản người dùng</MenuItem>
              {agencies.map(a => (
                <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Drop zone */}
          <Box
            className={`${classes.dropZone} ${isDragging ? classes.dropZoneActive : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={() => setIsDragging(false)}
            onClick={() => fileInputRef.current?.click()}
          >
            <PublishIcon style={{ fontSize: 36, color: '#81c784', marginBottom: 8 }} />
            <Typography style={{ fontWeight: 600, color: '#2e7d32', marginBottom: 4 }}>
              {selectedFile ? `✅ ${selectedFile.name}` : 'Kéo thả file .xlsx vào đây'}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {selectedFile
                ? `Kích thước: ${(selectedFile.size / 1024).toFixed(1)} KB`
                : 'hoặc click để chọn file'}
            </Typography>
          </Box>
          <input
            type="file"
            accept=".xlsx,.xls"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFilePick}
          />

          {/* Loading */}
          {isLoading && (
            <Box>
              <Typography style={{ fontSize: 13, color: '#2e7d32', marginBottom: 6 }}>
                Đang xử lý file...
              </Typography>
              <LinearProgress style={{ borderRadius: 4, height: 6 }} />
            </Box>
          )}

          {/* Result */}
          {isDone && importResult && (
            <Box className={classes.resultBox}>
              <Box display="flex" alignItems="center" style={{ gap: 6, marginBottom: 10 }}>
                <CheckCircleOutlineIcon style={{ color: '#2e7d32' }} />
                <Typography style={{ fontWeight: 700, color: '#2e7d32' }}>Nhập kho thành công!</Typography>
              </Box>
              <Divider style={{ marginBottom: 8 }} />
              <Box className={classes.resultItem}>
                <Typography color="textSecondary">Sản phẩm tạo mới:</Typography>
                <Typography style={{ fontWeight: 700, color: '#27ae60' }}>{importResult.created}</Typography>
              </Box>
              <Box className={classes.resultItem}>
                <Typography color="textSecondary">Sản phẩm cập nhật tồn:</Typography>
                <Typography style={{ fontWeight: 700, color: '#2980b9' }}>{importResult.updated}</Typography>
              </Box>
              <Box className={classes.resultItem}>
                <Typography color="textSecondary">Tổng SL nhập:</Typography>
                <Typography style={{ fontWeight: 700 }}>{importResult.quantity}</Typography>
              </Box>
              {importResult.errorCount > 0 && (
                <Box className={classes.resultItem}>
                  <Typography color="error">Dòng lỗi:</Typography>
                  <Typography style={{ fontWeight: 700, color: '#e74c3c' }}>{importResult.errorCount}</Typography>
                </Box>
              )}
              <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
                File báo cáo chi tiết đã được tải xuống tự động.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions style={{ padding: '12px 24px', gap: 8 }}>
        <Button onClick={onClose} variant="outlined" style={{ borderRadius: 8, textTransform: 'none' }}>
          {isDone ? 'Đóng' : 'Hủy'}
        </Button>
        {!isDone && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            className={classes.saveBtn}
            disabled={isLoading || !selectedFile}
            startIcon={isLoading ? <CircularProgress size={16} style={{ color: '#fff' }} /> : <PublishIcon />}
          >
            {isLoading ? 'Đang nhập...' : 'Bắt đầu nhập kho'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportExcelDialog;
